import { SyntheticEvent, useCallback, useState } from 'react';
import { Accordion as DSAccordion, IconButton, Button, Checkbox } from '@treezor/design-system';
import { useForm, Path, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  getAttribute,
  getCheckedAttributesValues,
  getParentAttribute,
  setAttributeValue,
} from '@/features/wallets/components/WalletOperations/operationsHelpers';
import { checkedValues, IAttributeConfig, TCheckedValues } from '@/features/wallets/store/models/wallet.model';

type TProps<T extends IAttributeConfig & { isChecked: TCheckedValues }> = {
  title: string;
  initialData: T[];
  currentdata?: T[];
  onClose: () => void;
  translations: Record<string, string>;
  onFiltersChange: (value: (T | IAttributeConfig)[]) => void;
  isModalOpen: boolean;
};

function AccordionFilter<T extends IAttributeConfig & { isChecked: TCheckedValues }>({
  initialData,
  title,
  onClose,
  translations,
  onFiltersChange,
  isModalOpen,
}: TProps<T>) {
  const { t } = useTranslation();

  const isGroupChecked = useCallback((attribute: T) => {
    if (attribute.childs) {
      if (attribute.childs.every((attr) => attr.isChecked === false || attr.isChecked === undefined)) {
        return false;
      } else if (attribute.childs.every((attr) => attr.isChecked === true)) {
        return true;
      } else {
        return checkedValues.INDETERMINATE;
      }
    } else {
      return attribute.isChecked;
    }
  }, []);

  const [filters, setFilters] = useState<T[]>(
    initialData.map((attribute) => ({ ...attribute, isChecked: isGroupChecked(attribute) })),
  );

  const [fieldvalues, setFieldValues] = useState<FieldValues>(getCheckedAttributesValues(filters));

  const { setValue, getValues } = useForm({ values: fieldvalues });

  const handleCheckOption = useCallback(
    (attribute: T | IAttributeConfig, isChecked: TCheckedValues, isEvent: boolean = true) => {
      setValue(attribute.name, isChecked);
      const parent = getParentAttribute<T>(filters, attribute.name);
      if (attribute.childs && isEvent) {
        attribute.childs = attribute.childs.map((child) => ({ ...child, isChecked }));
      }
      const newGroups = setAttributeValue(filters, attribute.name, 'isChecked', isChecked).map((attribute) => {
        if (attribute.name === parent?.name) {
          return {
            ...attribute,
            isChecked: isGroupChecked(attribute),
          };
        }
        return attribute;
      });

      setFieldValues(getCheckedAttributesValues(newGroups));
      setFilters(newGroups);
    },
    [filters, isGroupChecked, setValue],
  );

  const handleCheckboxChange = useCallback(
    (changeEvent: SyntheticEvent<HTMLInputElement>) => {
      const input = changeEvent.currentTarget;
      handleCheckOption(getAttribute(filters, input.name), input.checked);
    },
    [filters, handleCheckOption],
  );

  const handleAll = useCallback(
    (checked) => {
      filters.forEach((attribute) => {
        handleCheckOption(attribute, checked);
      });
    },
    [handleCheckOption, filters],
  );

  const handleReset = useCallback(() => {
    const initialGroupData = initialData.map((attribute) => ({ ...attribute, isChecked: isGroupChecked(attribute) }));
    setFieldValues(getCheckedAttributesValues(initialGroupData));
    setFilters(initialGroupData);
    onFiltersChange(initialGroupData);
    onClose();
  }, [onFiltersChange, onClose, initialData, isGroupChecked]);

  const handleSave = useCallback(() => {
    onFiltersChange(filters);
    onClose();
  }, [filters, onFiltersChange, onClose]);

  return (
    <div
      className={`fixed right-0 top-0 w-[25rem] h-full overflow-x-hidden overflow-y-auto bg-primary shadow-100 rounded-100 flex flex-col z-[201] transition-transform ${isModalOpen ? 'translate-x-0' : 'translate-x-full'}`}
    >
      <div className="flex flex-col gap-4 py-4 px-6 border-b border-b-secondary">
        <div className="flex items-center justify-between">
          <span className="typo-h6-bold text-secondary">{title}</span>
          <IconButton onClick={onClose} aria-label="close" icon="close" />
        </div>
        <div className="flex gap-3 items-center">
          <Button
            id="modal-action-select-all"
            size="s"
            variant="secondary"
            color="default"
            onClick={() => handleAll(true)}
            testId="filter-select-aLl"
          >
            {t('pages.cores.selectAll')}
          </Button>
          <Button
            id="modal-action-clear-all"
            size="s"
            variant="secondary"
            color="default"
            onClick={() => handleAll(false)}
            testId="filter-clear-all"
          >
            {t('pages.cores.clearAll')}
          </Button>
        </div>
      </div>
      <div className="bg-primary px-6 overflow-auto h-full">
        <DSAccordion
          accordionContent={filters.map((group) => ({
            header: (
              <Checkbox
                name={group.name as Path<T>}
                label={translations[group.name]}
                checked={getValues(group.name)}
                indeterminate={typeof group.isChecked === 'string' && group.isChecked === checkedValues.INDETERMINATE}
                onChange={handleCheckboxChange}
                data-test-id={group.name.replace('.', '-')}
              />
            ),
            children: (
              <div className="flex flex-col">
                {group.childs &&
                  group.childs.map((attribute: T) => {
                    return (
                      <Checkbox
                        key={attribute.name}
                        name={attribute.name as Path<T>}
                        checked={getValues(attribute.name)}
                        label={translations[attribute.name]}
                        onChange={handleCheckboxChange}
                        data-test-id={`${group.name.replace('.', '-')}.${attribute.name.replace('.', '-')}`}
                      />
                    );
                  })}
              </div>
            ),
          }))}
        />
      </div>
      <div className="flex items-center justify-center gap-4 border-t border-t-secondary p-4">
        <Button
          id="modal-filter-reset"
          variant="tertiary"
          color="default"
          onClick={handleReset}
          testId="modal-filter-reset"
        >
          {t('pages.cores.reset')}
        </Button>
        <Button
          id="modal-filter-save"
          variant="primary"
          color="default"
          onClick={handleSave}
          testId="modal-filter-save"
        >
          {t('pages.cores.saveButton')}
        </Button>
      </div>
    </div>
  );
}

export default AccordionFilter;
